import * as React from "react";
import { cn } from "@/lib/utils";
import { lazy } from "react";

interface ChartProps {
  data: any[];
  index: string;
  categories: string[];
  colors?: string[];
  valueFormatter?: (value: number) => string;
  className?: string;
}

const LazyResponsiveContainer = lazy(() => import('recharts').then(mod => ({ default: mod.ResponsiveContainer })));
const LazyAreaChart = lazy(() => import('recharts').then(mod => ({ default: mod.AreaChart })));
const LazyBarChart = lazy(() => import('recharts').then(mod => ({ default: mod.BarChart })));
const LazyLineChart = lazy(() => import('recharts').then(mod => ({ default: mod.LineChart })));
const LazyArea = lazy(() => import('recharts').then(mod => ({ default: mod.Area })));
const LazyBar = lazy(() => import('recharts').then(mod => ({ default: mod.Bar })));
const LazyLine = lazy(() => import('recharts').then(mod => ({ default: mod.Line })));
const LazyXAxis = lazy(() => import('recharts').then(mod => ({ default: mod.XAxis })));
const LazyYAxis = lazy(() => import('recharts').then(mod => ({ default: mod.YAxis })));
const LazyTooltip = lazy(() => import('recharts').then(mod => ({ default: mod.Tooltip })));

export const AreaChart: React.FC<ChartProps> = ({
  data,
  index,
  categories,
  colors = ["blue", "green", "red", "purple", "orange"],
  valueFormatter = (value: number) => `${value}`,
  className,
}) => {
  const colorMapping: Record<string, string> = {
    blue: "#3b82f6",
    green: "#10b981",
    red: "#ef4444",
    purple: "#8b5cf6",
    orange: "#f97316"
  };

  const getColorHex = (color: string) => {
    return colorMapping[color] || color;
  };

  return (
    <div className={cn("w-full h-full", className)}>
      <React.Suspense fallback={<div style={{height: 200}}>Loading chart…</div>}>
        <LazyResponsiveContainer width="100%" height="100%">
          <LazyAreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              {categories.map((category, i) => (
                <linearGradient key={category} id={`color-${category}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={getColorHex(colors[i % colors.length])} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={getColorHex(colors[i % colors.length])} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <LazyXAxis 
              dataKey={index} 
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
            />
            <LazyYAxis 
              hide={true}
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={valueFormatter}
            />
            <LazyTooltip 
              formatter={valueFormatter}
              contentStyle={{ 
                background: 'white', 
                border: '1px solid #f3f4f6',
                borderRadius: '0.375rem',
                fontSize: '0.75rem'
              }}
            />
            {categories.map((category, i) => (
              <LazyArea
                key={category}
                type="monotone"
                dataKey={category}
                stroke={getColorHex(colors[i % colors.length])}
                strokeWidth={2}
                fill={`url(#color-${category})`}
              />
            ))}
          </LazyAreaChart>
        </LazyResponsiveContainer>
      </React.Suspense>
    </div>
  );
};

export const BarChart: React.FC<ChartProps> = ({
  data,
  index,
  categories,
  colors = ["blue", "green", "red", "purple", "orange"],
  valueFormatter = (value: number) => `${value}`,
  className,
}) => {
  const colorMapping: Record<string, string> = {
    blue: "#3b82f6",
    green: "#10b981",
    red: "#ef4444",
    purple: "#8b5cf6",
    orange: "#f97316"
  };

  const getColorHex = (color: string) => {
    return colorMapping[color] || color;
  };

  return (
    <div className={cn("w-full h-full", className)}>
      <React.Suspense fallback={<div style={{height: 200}}>Loading chart…</div>}>
        <LazyResponsiveContainer width="100%" height="100%">
          <LazyBarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <LazyXAxis 
              dataKey={index} 
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
            />
            <LazyYAxis 
              hide={true}
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={valueFormatter}
            />
            <LazyTooltip 
              formatter={valueFormatter}
              contentStyle={{ 
                background: 'white', 
                border: '1px solid #f3f4f6',
                borderRadius: '0.375rem',
                fontSize: '0.75rem'
              }}
            />
            {categories.map((category, i) => (
              <LazyBar
                key={category}
                dataKey={category}
                fill={getColorHex(colors[i % colors.length])}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </LazyBarChart>
        </LazyResponsiveContainer>
      </React.Suspense>
    </div>
  );
};

export const LineChartComponent: React.FC<ChartProps> = ({
  data,
  index,
  categories,
  colors = ["blue", "green", "red", "purple", "orange"],
  valueFormatter = (value: number) => `${value}`,
  className,
}) => {
  const colorMapping: Record<string, string> = {
    blue: "#3b82f6",
    green: "#10b981",
    red: "#ef4444",
    purple: "#8b5cf6",
    orange: "#f97316"
  };

  const getColorHex = (color: string) => {
    return colorMapping[color] || color;
  };

  return (
    <div className={cn("w-full h-full", className)}>
      <React.Suspense fallback={<div style={{height: 200}}>Loading chart…</div>}>
        <LazyResponsiveContainer width="100%" height="100%">
          <LazyLineChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <LazyXAxis 
              dataKey={index} 
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
            />
            <LazyYAxis 
              hide={true}
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={valueFormatter}
            />
            <LazyTooltip 
              formatter={valueFormatter}
              contentStyle={{ 
                background: 'white', 
                border: '1px solid #f3f4f6',
                borderRadius: '0.375rem',
                fontSize: '0.75rem'
              }}
            />
            {categories.map((category, i) => (
              <LazyLine
                key={category}
                type="monotone"
                dataKey={category}
                stroke={getColorHex(colors[i % colors.length])}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
          </LazyLineChart>
        </LazyResponsiveContainer>
      </React.Suspense>
    </div>
  );
};
